import { type ReactElement } from 'react';

import { cn } from '@/lib/utils';

const CARET_W = 24;
const CARET_H = 12;

/**
 * Custom pointer for the profile popover (Radix PopoverArrow is often visibility:hidden).
 * Fill-only triangle (no stroke on the base) so it does not stack a second line on the panel top border;
 * slanted outer edges use thin paths so the outline still reads against the page.
 */
export function EatalyProfilePopoverCaret({ className }: { className?: string }): ReactElement {
    const tipX = CARET_W / 2;
    const tipY = 1.25;
    const bl = { x: 1.25, y: CARET_H - 0.75 };
    const br = { x: CARET_W - 1.25, y: CARET_H - 0.75 };

    return (
        <svg
            aria-hidden
            width={CARET_W}
            height={CARET_H}
            viewBox={`0 0 ${CARET_W} ${CARET_H}`}
            className={cn(
                'pointer-events-none block shrink-0 text-[var(--neutral-white)]',
                'drop-shadow-[0px_4px_12px_rgba(0,0,0,0.05)]',
                className
            )}>
            {/* Solid fill overlaps the panel top edge (parent uses negative margin) to hide border seam */}
            <polygon fill="currentColor" points={`${tipX},${tipY} ${br.x},${br.y} ${bl.x},${bl.y}`} />
            {/* Stroke only on the two visible legs — not along the base next to the panel */}
            <polyline
                fill="none"
                stroke="var(--sand-95)"
                strokeWidth={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                points={`${tipX},${tipY} ${br.x},${br.y}`}
            />
            <polyline
                fill="none"
                stroke="var(--sand-95)"
                strokeWidth={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                points={`${tipX},${tipY} ${bl.x},${bl.y}`}
            />
        </svg>
    );
}
